/**
 * Handles dynamic import failures caused by stale chunk hashes after deployments.
 * 
 * When a new version is deployed, the old HTML (cached in browser memory/bfcache)
 * references chunk filenames that no longer exist. This causes "Failed to fetch
 * dynamically imported module" errors.
 * 
 * Solution: Detect these errors and trigger a single page reload to get fresh HTML.
 * Uses sessionStorage to prevent infinite reload loops.
 */

const RELOAD_KEY = 'chunk-reload-attempted';
const RELOAD_EXPIRY_MS = 30_000; // 30 seconds

interface ReloadState {
  timestamp: number;
  url: string;
}

function getReloadState(): ReloadState | null {
  try {
    const stored = sessionStorage.getItem(RELOAD_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as ReloadState;
  } catch {
    return null;
  }
}

function setReloadState(): void {
  try {
    const state: ReloadState = {
      timestamp: Date.now(),
      url: window.location.href,
    };
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable, proceed anyway
  }
}

function clearReloadState(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
}

function shouldReload(): boolean {
  const state = getReloadState();
  if (!state) return true;
  
  // Allow reload if:
  // 1. The previous attempt was for a different URL (user navigated)
  // 2. The previous attempt was more than 30 seconds ago
  const isExpired = Date.now() - state.timestamp > RELOAD_EXPIRY_MS;
  const isDifferentUrl = state.url !== window.location.href;
  
  return isExpired || isDifferentUrl;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  
  // Vite/Rollup dynamic import errors
  if (message.includes('failed to fetch dynamically imported module')) return true;
  if (message.includes('loading chunk')) return true;
  if (message.includes('loading css chunk')) return true;
  
  // Network errors during chunk fetch
  if (message.includes('failed to load module script')) return true;
  
  // TypeErrors from failed imports
  if (error.name === 'TypeError' && message.includes('failed to fetch')) return true;
  
  return false;
}

/**
 * Attempts to recover from a chunk loading error by reloading the page.
 * Returns true if a reload was triggered, false if we should not reload
 * (e.g., already attempted recently).
 */
export function handleChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  
  console.warn('[chunk-reload] Detected stale chunk error:', error);
  
  if (!shouldReload()) {
    console.warn('[chunk-reload] Already attempted reload recently, not retrying');
    return false;
  }
  
  console.info('[chunk-reload] Reloading page to fetch fresh assets...');
  setReloadState();
  
  // Force reload bypassing cache
  window.location.reload();
  return true;
}

/**
 * Wraps a lazy import to handle chunk loading errors with automatic reload.
 */
export function lazyWithReload<T>(
  importFn: () => Promise<{ default: T }>
): () => Promise<{ default: T }> {
  return () =>
    importFn().catch((error) => {
      if (handleChunkLoadError(error)) {
        // Return a never-resolving promise since we're reloading
        return new Promise(() => {});
      }
      throw error;
    });
}

/**
 * Install global error handler for uncaught chunk loading errors.
 * Call this once at app startup.
 */
export function installChunkErrorHandler(): void {
  // Clear any stale reload state on successful page load
  clearReloadState();
  
  // Handle unhandled promise rejections (covers lazy() failures)
  window.addEventListener('unhandledrejection', (event) => {
    if (handleChunkLoadError(event.reason)) {
      event.preventDefault();
    }
  });
  
  // Handle synchronous errors (less common for dynamic imports)
  window.addEventListener('error', (event) => {
    if (handleChunkLoadError(event.error)) {
      event.preventDefault();
    }
  });
  
  console.debug('[chunk-reload] Error handler installed');
}
